import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IWebConfig } from 'src/app/shared/model/IWebConfig';
import { OTPChannelType } from 'src/app/shared/model/auth-channels';
import { CarouselComponent } from '../../components/carousel/carousel.component';
import { OtpComponent, OtpLoad } from '../../components/otp/otp.component';
import { STEPPER_IMPORTS, StepperComponent } from '../../components/stepper/stepper.component';
import { AppRoutes } from '../../routes/common.routes';
import { WebConfigService } from '../../services/web-config/web-config.service';
import { CredentialsStepComponent, CredentialsSubmission } from './credentials-step/credentials-step.component';
import { ForgotPasswordService } from './forgot-password.service';
import { PasswordResetStepComponent } from './password-reset-step/password-reset-step.component';
import { PinResetStepComponent } from './pin-reset-step/pin-reset-step.component';

@Component({
  selector: 'app-forgot-password',
  imports: [
    CarouselComponent,
    STEPPER_IMPORTS,
    OtpComponent,
    CredentialsStepComponent,
    PasswordResetStepComponent,
    PinResetStepComponent,
  ],
  providers: [ForgotPasswordService],
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  animations: [
    trigger('flyInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      transition('void => *', [style({ transform: 'translateX(100%)' }), animate('300ms ease-out')]),
      transition('* => void', [animate('300ms ease-out', style({ transform: 'translateX(-100%)' }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ForgotPasswordComponent {
  private readonly stepper = viewChild.required(StepperComponent);
  #forgotPasswordService = inject(ForgotPasswordService);
  private webconfig: IWebConfig = inject(WebConfigService);

  public portal = signal(this.webconfig.portal);
  public loginType = signal(this.webconfig.loginType);
  public loginMethod = signal(this.webconfig.loginMethod);

  public username = signal<string | undefined>(undefined);
  public channelType = signal<OTPChannelType | undefined>(undefined);
  public verifyGUID = signal<string | null>(null);

  public backgroundImage: string;

  constructor(public router: Router) {
    this.backgroundImage = this.webconfig.assets.authScreenBackground;
  }

  public navigateBack(): void {
    this.stepper().previous();
  }

  public async onCredentialsSubmitted(submission: CredentialsSubmission): Promise<void> {
    const accountExists = await this.#forgotPasswordService.checkAccountExists(submission.username);
    if (!accountExists) return;

    this.username.set(submission.username);
    this.channelType.set(submission.channelType);
    this.stepper().next();
  }

  public onVerified(otpLoad: OtpLoad): void {
    this.verifyGUID.set(otpLoad.id);
    this.stepper().next();
  }

  public async onResetPassword(password: string): Promise<void> {
    const success = await this.#forgotPasswordService.resetPassword(
      this.verifyGUID(),
      this.username(),
      this.channelType(),
      password,
      this.loginType(),
    );

    if (success) {
      this.router.navigate([AppRoutes.authLogin]);
    }
  }
}
